import { MAX_STEP_DURATION_IN_MINUTES } from 'src/components/pipelines/constants';

import { INode, Options } from '../types';

import { Collector } from './collector';
import {
  DEFAULT_ENVIRONMENTS,
  DEFINITION_SHAPE_DESCRIPTION,
  MAX_STEPS,
} from './const';
import {
  bool,
  cacheName,
  cacheReference,
  createEntityError,
  customPipelines,
  either,
  EntityError,
  EntityWarning,
  IEntity,
  destinationsOrImportOrSeqShape,
  importOrSeqShape,
  integer,
  map,
  natural,
  optional,
  pipelineVariablesSeq,
  runnerLabel,
  seq,
  serviceReference,
  shape,
  string,
  stringOrShapeEntity,
  YamlSyntaxError,
  ymlImport,
  enumEntity,
  outputVariables,
} from './entities';
import { serviceFilter, stageFilter, stepFilter } from './filters';
import { ParsingState } from './parsingState';
import {
  getAllowedSizes,
  getEffectiveMaxAllowedSizeForCloudRunners,
} from './util';
import { Converter } from './yaml';

const oidcShape = () =>
  shape({
    enable: bool(),
  });

const sparseCheckoutShape = () =>
  optional.shape({
    enabled: optional.bool(),
    'cone-mode': optional.bool(),
    patterns: optional.seq(string()),
  });

const stepCloneShape = () =>
  optional.shape({
    depth: optional.either(string('full'), natural()),
    lfs: optional.bool(),
    tags: optional.bool(),
    enabled: optional.bool(),
    'skip-ssl-verify': optional.bool(),
    filter: optional.cloneFilter(),
    'sparse-checkout': sparseCheckoutShape(),
  });

const rootEntityCloneShape = () =>
  optional.shape({
    depth: optional.either(string('full'), natural()),
    lfs: optional.bool(),
    tags: optional.bool(),
    enabled: optional.bool(),
    filter: optional.cloneFilter(),
    'sparse-checkout': sparseCheckoutShape(),
  });

const imageShape = () =>
  shape({
    name: optional.string(),
    username: optional.string(),
    password: optional.string(),
    email: optional.string(),
    'run-as-user': optional.integer(),
    aws: optional.either(
      shape({
        'access-key': string(),
        'secret-key': string(),
      }),
      shape({
        'oidc-role': string(),
      })
    ),
  });

const artifactUploadShape = (): IEntity =>
  shape({
    name: string(),
    type: optional.enum(['SHARED']),
    depth: optional.integer({
      min: 1,
    }),
    paths: seq(string()),
  });

const artifactsNewShape = (): IEntity =>
  shape(
    {
      paths: optional.seq(string()),
      upload: optional.seq(artifactUploadShape()),
      download: optional.either(bool(), seq(string())),
    },
    (node: INode): EntityError[] | null => {
      const { paths, upload } = node.result;
      if (paths && upload) {
        return createEntityError(
          node,
          `'paths' and 'upload' cannot be defined together. Only one of them can be defined under 'artifacts' at one time.`
        );
      }
      return null;
    }
  );

const artifactsShape = (): IEntity =>
  shape({
    download: optional.bool(),
    paths: optional.seq(string()),
  });

const cachesKeyShape = (): IEntity =>
  shape({
    files: seq(string()),
  });

const cachesShape = (): IEntity =>
  shape({
    key: cachesKeyShape(),
    path: string(),
  });

/**
 * Validate any pipeline variables that may be present.
 * @param node The YAML sequence node being validated.
 * @return EntityError or null if no errors.
 */
const validatePipelineVariables = (node: INode): EntityError[] => {
  const variables = node.children
    .map((element, index) => ({ element, index }))
    .filter(
      ({ element }) =>
        Object.keys(element.result).length === 1 &&
        Object.prototype.hasOwnProperty.call(element.result, 'variables')
    );

  if (variables.length > 1) {
    return createEntityError(
      variables[0].element,
      `'variables' can only be defined once per pipeline.`
    );
  }

  if (variables[0]) {
    if (variables[0].index > 0) {
      return createEntityError(
        variables[0].element,
        `'variables' must appear before steps or parallel steps.`
      );
    }

    const variableList = variables[0].element.result.variables;
    if (!Array.isArray(variableList)) {
      return createEntityError(
        variables[0].element,
        `'variables' must be a list.`
      );
    }

    if (
      variableList.length !==
      [...new Set(variableList.map((v: any) => JSON.stringify(v)))].map(
        (v: any) => JSON.parse(v)
      ).length
    ) {
      return createEntityError(
        variables[0].element,
        `The same variable name is used more than once.`
      );
    }
    if (variableList) {
      // com.atlassian.pipelines.plan.application.util.VariableParseUtil
      const regexp = new RegExp('^[A-Za-z_][A-Za-z0-9_]*$');

      for (const variable of variableList) {
        const name = variable ? variable.name : undefined;

        if (typeof name === 'string' && !regexp.test(name)) {
          return createEntityError(
            variables[0].element,
            `Variable name ${name} is invalid. ` +
              `It should contain only alphanumeric characters and underscores ` +
              `and it should not begin with a number.`
          );
        }
      }
    }
  }

  return null as any;
};

/**
 *
 * @param node The YAML sequence node being validated.
 * @return EntityError or null if no errors.
 */
const validateChangesetsCondition = (node: INode): EntityError[] => {
  const { includePaths, excludePaths } = node.result;
  if (!includePaths && !excludePaths) {
    return createEntityError(
      node,
      `At least one of 'includePaths' or 'excludePaths' must be present.`
    );
  }
  if (includePaths && excludePaths) {
    return createEntityError(
      node,
      `Only one of 'includePaths' or 'excludePaths' can be present.`
    );
  }
  return null as any;
};

/**
 * Validate any steps (including parallel steps) that may be present.
 * @param node The YAML sequence node being validated.
 * @return EntityError or null if no errors.
 */
const validateSteps = (options: Options, node: INode): EntityError[] => {
  const stepDefinitions = node.children;

  if (stepDefinitions.length > 0) {
    const firstStepNode = stepDefinitions[0];

    try {
      const { trigger } = firstStepNode.result.step;
      if (typeof trigger === 'string' && trigger.toLowerCase() === 'manual') {
        return createEntityError(
          firstStepNode,
          `The first step of a pipeline can't be manually triggered.`
        );
      }
      // eslint-disable-next-line no-empty
    } catch {}
  }

  const numberOfSteps = stepDefinitions
    .filter(definition => !!definition.result)
    .map(definition => {
      if (
        Object.prototype.hasOwnProperty.call(definition.result, 'parallel') &&
        Array.isArray(definition.result.parallel)
      ) {
        return definition.result.parallel.length;
      } else if (
        Object.prototype.hasOwnProperty.call(definition.result, 'parallel') &&
        Object.prototype.hasOwnProperty.call(
          definition.result.parallel,
          'steps'
        ) &&
        Array.isArray(definition.result.parallel.steps)
      ) {
        return definition.result.parallel.steps.length;
      } else if (
        Object.prototype.hasOwnProperty.call(definition.result, 'stage') &&
        Object.prototype.hasOwnProperty.call(
          definition.result.stage,
          'steps'
        ) &&
        Array.isArray(definition.result.stage.steps)
      ) {
        return definition.result.stage.steps.length;
      } else if (
        Object.prototype.hasOwnProperty.call(definition.result, 'step')
      ) {
        return 1;
      } else {
        return 0;
      }
    })
    .reduce((val, i) => i + val, 0);

  if (options.maxSteps && numberOfSteps > options.maxSteps) {
    return createEntityError(
      node,
      `You can't define more than ${options.maxSteps} steps in total.`
    );
  }

  return null as any;
};

const pipeShape = () =>
  shape(
    {
      pipe: optional.string(),
      task: optional.string(),
      name: optional.string(),
      environment: optional.map(string(), string()),
      variables: optional.map(
        string(),
        either(string(), seq(string()), integer())
      ),
    },
    null,
    'a pipe'
  );

const conditionShape = (options: Options) => {
  const changesetsShape = options.excludePathsChangesetConditionEnabled
    ? shape(
        {
          includePaths: optional.seq(string()),
          excludePaths: optional.seq(string()),
        },
        validateChangesetsCondition,
        'a changesets condition'
      )
    : shape(
        {
          includePaths: seq(string()),
        },
        null,
        'a changesets condition'
      );

  return shape(
    {
      changesets: changesetsShape,
    },
    null,
    'a condition'
  );
};

const runtimeShape = () => {
  const allowedVersionValues = [1, 2];
  return optional.shape({
    cloud: optional.shape({
      'atlassian-ip-ranges': optional.bool(),
      arch: optional.enum(['x86', 'arm']),
      version: optional.enum([
        ...allowedVersionValues,
        ...allowedVersionValues.map(v => v.toString()),
      ]),
    }),
  });
};
export const maxTimeShape = (options: Options): IEntity => {
  return optional.integer({
    min: 1,
    max: options.maxStepDuration,
  });
};

const onFailShape = () => {
  return optional.shape({
    strategy: enumEntity(['fail', 'ignore', 'retry']),
    maxRetryCount: optional.integer({ min: 1, max: 10 }),
  });
};

const stepShape = (options: Options): IEntity =>
  shape(
    {
      runtime: runtimeShape(),
      name: optional.string(),
      image: optional.either(string(), imageShape()),
      'max-time': maxTimeShape(options),
      size: optional.any(), // filtered in stepFilter
      script: seq(either(string(), pipeShape())),
      'after-script': optional.seq(either(string(), pipeShape())),
      artifacts: optional.seqOrShapeEntity(
        seq(string()),
        options.isNewArtifactConfigEnabled
          ? artifactsNewShape()
          : artifactsShape()
      ),
      caches: optional.seq(cacheReference()),
      'output-variables': optional.seq(outputVariables()),
      services: optional.seq(serviceReference(), false, serviceFilter),
      trigger: optional.either(
        string('manual', false),
        string('automatic', false)
      ),
      deployment: optional.environment(),
      environment: optional.environment(),
      clone: stepCloneShape(),
      condition: optional.of(conditionShape(options)),
      'runs-on': optional.seqOrStringEntity(
        seq(runnerLabel(options)),
        runnerLabel(options)
      ),
      oidc: optional.either(bool(), oidcShape()),
      'fail-fast': optional.bool(),
      'on-fail': onFailShape(),
    },
    stepFilter(options)
  );

const stageShape = (options: Options): IEntity =>
  shape(
    {
      name: optional.string(),
      deployment: optional.environment(),
      environment: optional.environment(),
      trigger: optional.either(
        string('manual', false),
        string('automatic', false)
      ),
      condition: optional.of(conditionShape(options)),
      steps: seq(
        shape({
          step: optional.of(stepShape(options)),
        })
      ),
      'on-fail': onFailShape(),
    },
    stageFilter()
  );

const stepsOrStages = (options: Options) =>
  seq(
    shape({
      variables: optional.of(pipelineVariablesSeq()),
      step: optional.of(stepShape(options)),
      stage: optional.of(stageShape(options)),
      parallel: optional.seqOrShapeEntity(
        seq(shape({ step: optional.of(stepShape(options)) })),
        shape({
          'fail-fast': optional.bool(),
          'on-fail': onFailShape(),
          steps: seq(shape({ step: optional.of(stepShape(options)) })),
        })
      ),
    }),
    true,
    (list: any) =>
      validatePipelineVariables(list) || validateSteps(options, list)
  );

const optionsSize = (options: Options) => {
  const effectiveMaxAllowedSizeForCloudRunners =
    getEffectiveMaxAllowedSizeForCloudRunners(options);
  const sizeRange = getAllowedSizes(effectiveMaxAllowedSizeForCloudRunners);
  const sizeEntities = sizeRange.map((size, index) => {
    const skipDescription = index < sizeRange.length - 1;
    return string(size, false, skipDescription);
  });
  return optional.either(...sizeEntities);
};

const rootEntity = (options: Options) =>
  shape({
    image: optional.either(string(), imageShape()),
    export: optional.bool(),
    clone: rootEntityCloneShape(),
    options: optional.shape({
      runtime: runtimeShape(),
      docker: optional.bool(),
      'max-time': maxTimeShape(options),
      size: optionsSize(options),
    }),
    definitions: optional.shape(
      {
        services: optional.map(
          string(),
          shape({
            image: optional.either(string(), imageShape()),
            environment: optional.map(string(), string()),
            variables: optional.map(string(), string()),
            memory: optional.integer({ min: 128 }),
            type: optional.either(string('docker', true, true)),
          }),
          (
            keyNode: INode,
            serviceKey: string,
            serviceValue: INode,
            _: ParsingState
          ) => {
            if (
              serviceKey === 'docker' ||
              serviceValue.result?.type === 'docker'
            ) {
              return null;
            }
            if (!serviceValue.result) {
              return createEntityError(
                keyNode,
                `You need to tell us which image to use.`
              );
            } else if (
              !Object.prototype.hasOwnProperty.call(
                serviceValue.result,
                'image'
              )
            ) {
              return createEntityError(
                keyNode,
                `You need to tell us which image to use.`
              );
            } else {
              return null;
            }
          }
        ),
        caches: optional.map(
          cacheName(),
          stringOrShapeEntity(string(), cachesShape())
        ),
        pipelines: optional.of(customPipelines(stepsOrStages(options))),
      },
      null,
      DEFINITION_SHAPE_DESCRIPTION
    ),
    pipelines: shape({
      branches: optional.map(
        string(),
        importOrSeqShape(ymlImport(), stepsOrStages(options))
      ),
      bookmarks: optional.map(
        string(),
        importOrSeqShape(ymlImport(), stepsOrStages(options))
      ),
      custom: optional.of(
        customPipelines(importOrSeqShape(ymlImport(), stepsOrStages(options)))
      ),
      tags: optional.map(
        string(),
        importOrSeqShape(ymlImport(), stepsOrStages(options))
      ),
      'pull-requests': optional.map(
        string(),
        destinationsOrImportOrSeqShape(
          shape({
            destinations: map(
              string(),
              importOrSeqShape(ymlImport(), stepsOrStages(options))
            ),
          }),
          ymlImport(),
          stepsOrStages(options)
        )
      ),
      default: optional.of(
        importOrSeqShape(ymlImport(), stepsOrStages(options))
      ),
    }),
  });

export function validateYaml(
  bitbucketPipelinesYml: string,
  options: Options = {
    maxStepDuration: MAX_STEP_DURATION_IN_MINUTES,
    maxSteps: MAX_STEPS,
    environments: DEFAULT_ENVIRONMENTS,
    effectiveVariablesInYamlEnabled: false,
    maximumAllowedStepSizeForCloudRunners: 16,
    excludePathsChangesetConditionEnabled: false,
    isNewArtifactConfigEnabled: false,
  }
): (YamlSyntaxError | EntityError | EntityWarning)[] {
  const converter = new Converter(bitbucketPipelinesYml);
  const parsingState = new ParsingState(options);
  const result = converter.toTree(bitbucketPipelinesYml);

  if (result.entityErrorType === 'yaml-syntax-error') {
    return [result as YamlSyntaxError];
  }
  if (converter.hasAnchor) {
    return []; // ignore errors
  }

  parsingState.setJSObject(converter.JSObject);
  const errs = rootEntity(options).validate(result, parsingState);
  if (errs !== undefined) {
    return Collector.from<EntityError | EntityWarning>(errs);
  }
  return [];
}
